/**
 * The shortcut sheet, over the score.
 *
 * An editor whose keyboard is its fastest surface has to say what the keyboard does
 * somewhere other than a manual. It is a dialog rather than a panel because it is read once
 * and then dismissed, and Escape or a click anywhere closes it.
 */

import { useEffect } from "react";
import { SHORTCUTS } from "../lib/score-help";

export interface ScoreHelpProps {
  onClose(): void;
}

export default function ScoreHelp({ onClose }: ScoreHelpProps): JSX.Element {
  useEffect(() => {
    const escape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" && event.key !== "?") return;
      // Said, so an editor filling the window stays filled when only the keys were closed.
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/25 p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-3xl rounded-2xl border border-rule bg-white p-6 shadow-[0_24px_64px_rgba(0,0,0,0.18)]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-quiet">
              Keyboard
            </p>
            <h2 className="mt-1 text-lg text-ink">Everything the keys do</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-[10px] border border-rule px-3 text-[13px] text-ink-faded hover:border-ink-quiet hover:text-ink"
          >
            Close
          </button>
        </div>

        <div className="mt-5 grid gap-x-8 gap-y-5 sm:grid-cols-2">
          {SHORTCUTS.map((group) => (
            <section key={group.title} className="space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-quiet">
                {group.title}
              </p>
              <dl className="space-y-1">
                {group.keys.map((shortcut) => (
                  <div key={shortcut.keys} className="flex gap-3 text-[13px]">
                    <dt className="w-[132px] shrink-0 font-mono text-[11px] text-ink">
                      {shortcut.keys}
                    </dt>
                    <dd className="text-ink-faded">{shortcut.what}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
